/**
 * Extern Labs — the pure scoring rules shared by the screens.
 *
 * No React, no pack imports beyond types: everything here is a pure function of (content, answers),
 * so it is exhaustively unit-testable and identical for every vertical. The finance versions of these
 * live scattered across `_data/m1-cards.ts` (`computeM1Lean`), `_data/m3-numbers.ts`
 * (`isRatioCorrect`) and `_partials/m4-activity.tsx` (`isCorrectPick`, inline).
 */

import type { LabsAnswerKey, LabsDerivedMetric, LabsLeanBucket, LabsSwipeCard } from './types';

// ---------------------------------------------------------------------------
// M1 — which pole did the learner lean to?
// ---------------------------------------------------------------------------

export interface LabsLean {
  /** Cards swiped right that belong to pole A. */
  aCount: number;
  /** Share of right-swipes leaning to pole A, 0–100. 50 when nothing was swiped right. */
  aPct: number;
  bCount: number;
  bPct: number;
  bucket: LabsLeanBucket;
  /**
   * Total right-swipes. Zero is NOT the same as an even split, and the bucket alone cannot tell them
   * apart: both arrive as `split`. Callers that care about the difference read this.
   */
  rightCount: number;
}

/** Bucket thresholds on `aPct`. Mirrors finance's `computeM1Lean` exactly. */
function leanBucket(aPct: number): LabsLeanBucket {
  if (aPct >= 65) {
    return 'strongA';
  }
  if (aPct <= 35) {
    return 'strongB';
  }
  if (aPct > 55) {
    return 'moderateA';
  }
  if (aPct < 45) {
    return 'moderateB';
  }
  return 'split';
}

/**
 * Tally the learner's right-swipes by pole.
 *
 * Only RIGHT swipes ("that's me") count — a left swipe is a rejection, not a vote for the other pole.
 * A learner who swiped right on nothing has no signal, so they land at a dead-even 50/50 `split`
 * rather than dividing by zero.
 */
export function computeLean(
  cards: readonly LabsSwipeCard[],
  answers: Readonly<Record<string, 'left' | 'right'>>,
): LabsLean {
  let aCount = 0;
  let bCount = 0;
  for (const card of cards) {
    if (answers[card.id] !== 'right') {
      continue;
    }
    if (card.pole === 'a') {
      aCount += 1;
    } else {
      bCount += 1;
    }
  }

  const total = aCount + bCount;
  const aPct = total === 0 ? 50 : Math.round((aCount / total) * 100);
  const bPct = 100 - aPct;

  return { aCount, aPct, bCount, bPct, bucket: leanBucket(aPct), rightCount: total };
}

// ---------------------------------------------------------------------------
// M2 — the slider
// ---------------------------------------------------------------------------

/** The 7 slider stops (0–6). The centre stop (3) is "no strong lean". */
export const SLIDER_STOPS = 7;
export const SLIDER_CENTER = 3;

/**
 * Which side of the axis a slider position votes for. Positions 0–2 score the LEFT anchor, 4–6 the
 * RIGHT, and the exact centre (3) votes for neither.
 */
export function sliderSide(value: number): 'center' | 'left' | 'right' {
  if (value < SLIDER_CENTER) {
    return 'left';
  }
  if (value > SLIDER_CENTER) {
    return 'right';
  }
  return 'center';
}

// ---------------------------------------------------------------------------
// M3 — grading a derived metric
// ---------------------------------------------------------------------------

/**
 * True when `value` is within `tolerance` of the metric's correct answer.
 *
 * Graded to a tolerance rather than by equality because the learner is computing a number by hand:
 * finance allows ±1.0 so a rounding difference (37.5 vs 37.6) still counts as understanding.
 */
export function isMetricCorrect(metric: LabsDerivedMetric, value: number, tolerance: number): boolean {
  return Math.abs(value - metric.correct) <= tolerance;
}

// ---------------------------------------------------------------------------
// M4 — grading a read
// ---------------------------------------------------------------------------

/**
 * True when the learner's chip is the intended read for that group.
 *
 * A table LOOKUP, not a computation: the intended read of each subject is authored by a human in the
 * pack's answer key, not derived at runtime from the subject's numbers. Ungraded (interpretive)
 * groups never reach here — the screen shows them a neutral "judgment call" note instead.
 */
export function isCorrectPick(answerKey: LabsAnswerKey | undefined, groupId: string, chipId: string): boolean {
  return answerKey?.[groupId] === chipId;
}
